import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ArrowDown, ArrowUp, MapPin, Navigation, Route } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const fieldCls = "rounded-xl border-2 border-monster-navy bg-white px-3 py-2 text-sm font-semibold shadow-sticker-sm";

const buildMapsUrl = (start, stops) => {
    const enc = (s) => encodeURIComponent(s);
    const origin = start.trim() ? enc(start.trim()) : enc(stops[0].address);
    const destination = enc(stops[stops.length - 1].address);
    const middle = stops.slice(start.trim() ? 0 : 1, -1).map((s) => enc(s.address));
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    if (middle.length > 0) url += `&waypoints=${middle.join("|")}`;
    return url;
};

export const AdminRoutes = () => {
    const [bookings, setBookings] = useState([]);
    const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
    const [order, setOrder] = useState([]);
    const [excluded, setExcluded] = useState([]);
    const [start, setStart] = useState(() => localStorage.getItem("mi_route_start") || "");
    const [optimizing, setOptimizing] = useState(false);
    const [optResult, setOptResult] = useState(null);

    useEffect(() => {
        api.get("/bookings")
            .then((res) => setBookings(res.data))
            .catch((err) => toast.error(formatApiError(err)));
    }, []);

    const dayStops = useMemo(
        () => bookings.filter((b) => b.event_date === date && ["new", "confirmed"].includes(b.status)),
        [bookings, date]
    );

    useEffect(() => {
        setOrder(dayStops.map((b) => b.id));
        setExcluded([]);
    }, [dayStops]);

    const stops = order.map((id) => dayStops.find((b) => b.id === id)).filter(Boolean);
    const activeStops = stops.filter((s) => !excluded.includes(s.id));

    const move = (index, dir) => {
        const next = [...order];
        const swap = index + dir;
        if (swap < 0 || swap >= next.length) return;
        [next[index], next[swap]] = [next[swap], next[index]];
        setOrder(next);
    };

    const toggle = (id) => setExcluded((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

    const saveStart = (v) => {
        setStart(v);
        localStorage.setItem("mi_route_start", v);
    };

    const mapsUrl = activeStops.length > 0 ? buildMapsUrl(start, activeStops) : null;

    const autoSort = async () => {
        setOptimizing(true);
        try {
            const res = await api.post("/routes/optimize", {
                depot: start || null,
                stops: activeStops.map((s) => ({ id: s.id, address: s.address })),
            });
            const remaining = order.filter((id) => !res.data.ordered_ids.includes(id));
            setOrder([...res.data.ordered_ids, ...remaining]);
            setOptResult(res.data);
            toast.success("Stops sorted shortest-drive-first");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setOptimizing(false);
        }
    };

    const optSummary = () => {
        if (!optResult || !optResult.distance_meters) return null;
        const miles = (optResult.distance_meters / 1609.344).toFixed(1);
        const mins = Math.round(parseInt(optResult.duration || "0", 10) / 60);
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${miles} mi round trip · about ${h > 0 ? `${h}h ` : ""}${m}m of driving`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="space-y-8"
            data-testid="admin-routes"
        >
            <div className="rounded-3xl border-2 border-monster-navy bg-white p-6 shadow-sticker sm:p-8">
                <h2 className="font-display text-xl font-semibold text-monster-navy">Delivery Route Planner</h2>
                <p className="mt-1 text-sm font-semibold text-monster-navy/50">
                    Pick a party day, arrange your stops, then open the full route in Google Maps.
                </p>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div>
                        <Label htmlFor="route-date" className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Party date</Label>
                        <Input id="route-date" data-testid="route-date-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={fieldCls} />
                    </div>
                    <div>
                        <Label htmlFor="route-start" className="mb-1.5 block font-display text-xs font-semibold text-monster-navy/60">Start from (optional — your shop/home)</Label>
                        <Input
                            id="route-start"
                            data-testid="route-start-input"
                            value={start}
                            onChange={(e) => saveStart(e.target.value)}
                            placeholder="e.g. 123 Main St, Flint, MI"
                            className={fieldCls}
                        />
                    </div>
                </div>
            </div>

            <div className="rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b-2 border-monster-navy px-6 py-4">
                    <h3 className="font-display text-lg font-semibold text-monster-navy">
                        Stops for {format(new Date(`${date}T12:00:00`), "EEEE, MMMM d, yyyy")}
                    </h3>
                    <span className="rounded-full border-2 border-monster-navy bg-monster-yellow px-4 py-1.5 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm">
                        {activeStops.length} stop{activeStops.length === 1 ? "" : "s"}
                    </span>
                </div>
                {stops.length === 0 ? (
                    <p data-testid="routes-empty" className="p-10 text-center font-semibold text-monster-navy/50">
                        No bookings on this date. Try another day.
                    </p>
                ) : (
                    <ol className="divide-y-2 divide-monster-navy/10">
                        {stops.map((s, i) => {
                            const off = excluded.includes(s.id);
                            return (
                                <li
                                    key={s.id}
                                    data-testid={`route-stop-${s.id}`}
                                    className={`flex flex-wrap items-center gap-3 px-5 py-4 sm:flex-nowrap ${off ? "opacity-40" : ""}`}
                                >
                                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-monster-navy font-display text-sm font-semibold shadow-sticker-sm ${s.status === "confirmed" ? "bg-monster-blue text-white" : "bg-monster-yellow text-monster-navy"}`}>
                                        {i + 1}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <MapPin className="h-4 w-4 shrink-0 text-monster-red" />
                                            <span className="truncate font-bold text-monster-navy">{s.address}</span>
                                        </div>
                                        <div className="mt-0.5 pl-6 text-xs font-semibold text-monster-navy/55">
                                            {s.inflatable_name} · {s.name} · {s.phone} · <span className="capitalize">{s.status}</span>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <button data-testid={`route-up-${s.id}`} onClick={() => move(i, -1)} disabled={i === 0} className="rounded-lg border-2 border-monster-navy bg-white p-1.5 shadow-sticker-sm transition-transform hover:-translate-y-0.5 disabled:opacity-30" aria-label="Move up">
                                            <ArrowUp className="h-4 w-4" />
                                        </button>
                                        <button data-testid={`route-down-${s.id}`} onClick={() => move(i, 1)} disabled={i === stops.length - 1} className="rounded-lg border-2 border-monster-navy bg-white p-1.5 shadow-sticker-sm transition-transform hover:-translate-y-0.5 disabled:opacity-30" aria-label="Move down">
                                            <ArrowDown className="h-4 w-4" />
                                        </button>
                                        <button
                                            data-testid={`route-toggle-${s.id}`}
                                            onClick={() => toggle(s.id)}
                                            className={`rounded-lg border-2 border-monster-navy px-3 py-1.5 font-display text-xs font-semibold shadow-sticker-sm ${off ? "bg-white text-monster-navy/50" : "bg-monster-green text-monster-navy"}`}
                                        >
                                            {off ? "Skipped" : "In Route"}
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                )}
                {stops.length > 0 && (
                    <div className="border-t-2 border-monster-navy px-6 py-5">
                        {activeStops.length > 10 && (
                            <p className="mb-3 rounded-xl border-2 border-monster-red bg-red-50 px-4 py-2 text-sm font-bold text-monster-red">
                                Google Maps handles up to 10 stops per route — split this day into two routes.
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-3">
                            <button
                                data-testid="route-autosort-btn"
                                onClick={autoSort}
                                disabled={optimizing || activeStops.length < 2}
                                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-monster-navy bg-monster-yellow px-6 py-4 font-display text-base font-semibold text-monster-navy shadow-sticker transition-all hover:-translate-y-0.5 active:translate-y-1 active:shadow-none disabled:opacity-50"
                            >
                                <Route className="h-5 w-5" /> {optimizing ? "Optimizing..." : "Auto-Sort Shortest Drive"}
                            </button>
                            <a
                                data-testid="route-open-maps-btn"
                                href={mapsUrl || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center justify-center gap-2 rounded-full border-2 border-monster-navy bg-monster-blue px-6 py-4 font-display text-base font-semibold text-white shadow-sticker transition-all hover:-translate-y-0.5 hover:bg-[#1a63c2] active:translate-y-1 active:shadow-none ${!mapsUrl ? "pointer-events-none opacity-50" : ""}`}
                            >
                                <Navigation className="h-5 w-5" /> Open Route in Google Maps
                            </a>
                        </div>
                        {optResult && optResult.distance_meters && (
                            <p data-testid="route-optimize-result" className="mt-3 rounded-xl border-2 border-monster-navy bg-[#E7F9EC] px-4 py-2 text-sm font-bold text-monster-navy">
                                Optimized route: {optSummary()}
                            </p>
                        )}
                        <p className="mt-3 text-xs font-semibold text-monster-navy/45">
                            Tip: order stops with the arrows (or skip a stop) before opening — Google Maps will follow your order exactly.
                        </p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};
